<template>
    <div>
        <transition
            name="fade"
            mode="out-in"
        >
            <TabsPanelSpinner v-if="initialLoadInProgress" />

            <div v-else>
                <div class="flex items-center flex-nowrap bg-panel border py-2 md:py-3 px-4 mb-5 md:rounded-lg gap-3">
                    <span class="grow">
                        {{ documentsTotalCount }}
                        {{
                            t('document', {
                                count: documentsTotalCount,
                            })
                        }}
                    </span>

                    <CourseShowDocumentsFilter @changed="filterChanged" />
                    <BtnMenu
                        size="sm"
                        variant="outlined"
                        :label="t('filter.sort_by.documents.' + currentSort)"
                        right-icon="chevron-down"
                        :options="options"
                        :menu-title="t('documents.sorting.header')"
                        menu-placement="bottom-end"
                        class="!font-normal bg-white"
                    />
                </div>

                <transition
                    name="fade"
                    mode="out-in"
                >
                    <div v-if="emptyFeed">
                        <DashboardCarousel
                            v-if="recommendedDocuments.length"
                            icon="document"
                            :title="t('dashboard:widgets.recommended_documents.title')"
                            class="my-4 shadow"
                            show-nav-button-on-hover
                        >
                            <CardDashboard
                                v-for="document in recommendedDocuments"
                                :key="document.fileId"
                                :card-title="document.fileName"
                                :preview-image="document.previewUrl"
                                :link="document.url"
                                class="w-44 min-w-44"
                            >
                                <TagDocumentType
                                    :type="document.typeId"
                                    :size="TagSize.SM"
                                    class="absolute top-2 right-2 z-2"
                                />
                            </CardDashboard>
                        </DashboardCarousel>
                        <EmptyFeed>
                            <template #image>
                                <img
                                    :src="emptyStateImage"
                                    class="w-48 mx-auto"
                                />
                            </template>
                            <template #text>
                                {{ t(`emptyFeed.documents_${keOrUserMessage}.text`) }}
                                <a
                                    v-if="keOrUserMessage === 'noSlots'"
                                    :href="route('reward.list')"
                                    class="text-blue-600"
                                >
                                    {{ t(`emptyFeed.reward`) }}
                                </a>
                            </template>
                            <template #button>
                                <BtnCta
                                    :href="emptyStateButtonLink"
                                    :label="t(`emptyFeed.documents_${keOrUserMessage}.cta`)"
                                />
                            </template>
                        </EmptyFeed>
                    </div>

                    <EmptySearchResult v-else-if="documentsTotalCount === 0" />

                    <transition-group
                        v-else-if="documentsTotalCount > 0"
                        tag="div"
                        name="fade"
                        class="flex flex-col justify-center space-y-2 mb-5 max-md:container"
                    >
                        <component
                            :is="componentType(item)"
                            v-for="item in documentsList"
                            :key="`${item.type}-${item.id ?? item.adType}`"
                            v-bind="componentProps(item)"
                            v-memo="[cardMode]"
                            @navigate="trackClick(item.id)"
                        ></component>

                        <BtnCta
                            v-if="documentsCanLoadMore"
                            class="mx-auto !mt-5"
                            icon="reload"
                            :label="t('loadMore')"
                            :loading="documentsProgress"
                            :disabled="documentsProgress"
                            @click="loadMore"
                        />
                    </transition-group>
                </transition>
            </div>
        </transition>
    </div>
</template>

<script>
import { computed } from 'vue';
import { mapGetters } from 'vuex';
import EmptyFeed from '@components/EmptyFeed';
import EmptySearchResult from '@components/EmptySearchResult';
import CardSearchResult from '@components/CardSearchResult';
import TabsPanelSpinner from '@components/TabsPanelTab/TabsPanelSpinner.vue';
import { Xandr, XandrStickyAd } from '@/components/Xandr';
import { trackImpression, trackClick } from '@/modules/tracking';
import { getContentTypesValues } from '@/utils/documentUtils';
import route from '@router/route';
import { getRecommendedDocuments } from '@/api/gateway/legacy-api/feed';
import DashboardCarousel from '@components/DashboardCarousel/DashboardCarousel.vue';
import CardDashboard from '@components/CardDashboard/CardDashboard.vue';
import TagDocumentType from '@components/TagDocumentType/TagDocumentType.vue';
import { TagSize } from '@components/Tag';
import { StudyMaterialsSortType } from '@api/gateway/legacy-api/search';
import { sortLabel } from '@/utils/searchUtils';
import { useScreenSize } from '@/composables/screenSize';
import { getDocumentLink } from '@/router/links';
import { useTranslation } from 'i18next-vue';
import CourseShowDocumentsFilter from './CourseShowDocumentsFilter.vue';

export default {
    name: 'CourseShowDocuments',
    components: {
        TagDocumentType,
        CardDashboard,
        DashboardCarousel,
        CourseShowDocumentsFilter,
        EmptyFeed,
        EmptySearchResult,
        CardSearchResult,
        TabsPanelSpinner,
        Xandr,
    },
    setup() {
        const { t } = useTranslation(['course', 'dashboard']);
        return {
            cardMode: computed(() => (useScreenSize().maxSM.value ? 'stack' : 'default')),
            t,
        };
    },
    data() {
        return {
            currentPage: 1,
            emptyFeed: false,
            currentFilter: {},
            currentSort: StudyMaterialsSortType.Downloads,
            initialLoadInProgress: true,
            keTracking: {
                utm_source: 'studydrive',
                utm_medium: 'banner',
                utm_campaign: '1905sdaa_3_empty_course',
            },
            searchId: Math.random().toString(36).substring(2, 11),
            recommendedDocuments: [],
            TagSize,
        };
    },
    computed: {
        ...mapGetters('course', [
            'documentsList',
            'documentsProgress',
            'documentsCanLoadMore',
            'documentsTotalCount',
            'course',
        ]),
        ...mapGetters('auth', ['isLoggedIn']),
        keOrUserMessage() {
            if (this.course.hasKeSlots) return 'hasSlots';
            return 'noSlots';
        },
        emptyStateImage() {
            if (this.course.hasKeSlots) return '/images/mascots/money.png';
            return '/images/mascots/sleep.png';
        },
        emptyStateButtonLink() {
            if (this.course.hasKeSlots) {
                return route('ke.index', this.keTracking);
            }

            return route('document.universities.create');
        },
        options() {
            return [
                {
                    label: sortLabel(StudyMaterialsSortType.VoteRating),
                    onClick: () => this.sortChanged(StudyMaterialsSortType.VoteRating),
                },
                {
                    label: sortLabel(StudyMaterialsSortType.Downloads),
                    onClick: () => this.sortChanged(StudyMaterialsSortType.Downloads),
                },
                {
                    label: sortLabel(StudyMaterialsSortType.Latest),
                    onClick: () => this.sortChanged(StudyMaterialsSortType.Latest),
                },
                {
                    label: sortLabel(StudyMaterialsSortType.Rating),
                    onClick: () => this.sortChanged(StudyMaterialsSortType.Rating),
                },
            ];
        },
    },
    mounted() {
        this.getDocuments(false);
    },
    methods: {
        route,
        componentProps(item) {
            switch (item.type) {
                case 'xandrAd': {
                    return {
                        xandrWrapper: `${item.adType}Wrapper`,
                        xandrSlot: item.adType,
                        xandrSizes: item.sizes,
                        adOnDemand: true,
                    };
                }
                case 'xandrStickyAd': {
                    return {
                        xandrWrapper: `${item.adType}Wrapper`,
                        xandrSlot: item.adType,
                        xandrSizes: item.sizes,
                        adOnDemand: true,
                    };
                }
                default:
                    return {
                        context: {
                            type: 'document',
                            avg_star_score: item.avg_star_score,
                            ratings_count: item.ratings_count,
                            user_rated: item.user_rated,
                            questions: item.number_questions,
                            has_ai_content: item.has_ai_content,
                            downloads: item.downloads,
                        },
                        createdAt: item.uploaded_at,
                        type: item.file_content_type,
                        imgSrc: item.file_preview_link,
                        title: item.filename,
                        titleTag: 'h2',
                        href: getDocumentLink(item.id, item.filename),
                        userName: item.user_data?.name,
                        userId: item.user_data?.id,
                        userKarma: item.user_data?.karma_points,
                        clickable: true,
                        enableLinks: true,
                        mode: this.cardMode,
                    };
            }
        },
        componentType(document) {
            switch (document.type) {
                case 'xandrAd':
                    return Xandr;
                case 'xandrStickyAd':
                    return XandrStickyAd;
                default:
                    return CardSearchResult;
            }
        },
        getDocuments(shouldAppendResult) {
            this.$store
                .dispatch('course/retrieveDocuments', {
                    filter: {
                        course_id: this.course.id,
                        university_id: this.course.university.id,
                        sort_type_documents: this.currentSort,
                        ...this.currentFilter,
                    },
                    page: this.currentPage,
                    shouldAppendResult,
                })
                .then(() => {
                    if (this.initialLoadInProgress && this.documentsTotalCount === 0) {
                        this.emptyFeed = true;
                        this.loadRecommendedDocuments();
                    }
                    this.initialLoadInProgress = false;

                    trackImpression('Search', {
                        query_phrase: this.currentFilter.phrase,
                        url: document.location.href,
                        nb_results: this.documentsTotalCount,
                        document_type: getContentTypesValues(this.currentFilter.document_type),
                        sort_type: this.currentSort,
                        additional_data: JSON.stringify({
                            filter: {
                                min_semester: this.currentFilter.min_semester,
                                max_semester: this.currentFilter.max_semester,
                            },
                        }),
                        is_active_search: false,
                        search_uuid: this.searchId,
                    });
                });
        },
        loadMore() {
            if (!this.documentsProgress) {
                this.currentPage += 1;
                this.getDocuments(true);
            }
        },
        filterChanged(filter) {
            this.currentPage = 1;
            this.currentFilter = filter;
            this.getDocuments(false);
        },
        sortChanged(sort) {
            this.currentPage = 1;
            this.currentSort = sort;
            this.getDocuments(false);
        },
        trackClick(id) {
            trackClick('SearchResults', {
                search_uuid: this.searchId,
                element_id: id,
            });
        },
        loadRecommendedDocuments() {
            if (!this.isLoggedIn) return;
            getRecommendedDocuments()
                .then((response) => {
                    response.data.forEach((item) => {
                        this.recommendedDocuments.push({
                            courseLink: item.course_link,
                            courseName: item.course_name,
                            fileId: item.file_id,
                            fileName: item.file_name,
                            previewUrl: item.preview_link,
                            url: item.link,
                            typeId: item.file_type,
                        });
                    });
                })
                .catch();
        },
    },
};
</script>
